import governmentDocsJson from '../data/government-documents.json';
import { GovernmentPublishedDocument } from '../types/governmentDocument';
import { ControlObligation, StatutoryProvision } from '../types/obligation';

export const GOVERNMENT_DOCUMENTS: GovernmentPublishedDocument[] = governmentDocsJson as GovernmentPublishedDocument[];

export function getAllGovernmentDocuments(): GovernmentPublishedDocument[] {
  return GOVERNMENT_DOCUMENTS;
}

export function getGovernmentDocumentById(id: string): GovernmentPublishedDocument | undefined {
  return GOVERNMENT_DOCUMENTS.find(doc => doc.id === id);
}

/**
 * Resolves an obligation (Control or Provision) to its primary Government Published Document
 */
export function getGovernmentDocumentForItem(
  item: ControlObligation | StatutoryProvision,
  isControl: boolean = false
): GovernmentPublishedDocument {
  // If StatutoryProvision
  if (!isControl && 'isRule' in item) {
    const prov = item as StatutoryProvision;
    if (prov.isRule || (prov.id && prov.id.startsWith('RULE-'))) {
      return getGovernmentDocumentById('DOC-RULES-2025') || GOVERNMENT_DOCUMENTS[1];
    }
    return getGovernmentDocumentById('DOC-ACT-2023') || GOVERNMENT_DOCUMENTS[0];
  }

  // If ControlObligation
  const ctrl = item as ControlObligation;
  const scope = (ctrl.scope || ctrl.provision || ctrl.source_scope || '').toLowerCase();
  const canonicalIds = ctrl.canonical_ids || [];

  // Check if it's primarily derived from Rules
  const hasRule = canonicalIds.some(id => id.startsWith('RULE-')) || scope.includes('r.') || scope.includes('rule');
  if (hasRule) {
    return getGovernmentDocumentById('DOC-RULES-2025') || GOVERNMENT_DOCUMENTS[1];
  }

  // Check if it relates to CERT-In or incident response (OBL-26, OBL-27)
  if (ctrl.id === 'OBL-26' || ctrl.id === 'OBL-27' || (ctrl.domain_slug === 'breach' && scope.includes('6-hour'))) {
    // Primary is still DPDP Act, but has CERT-In cross-reference
    return getGovernmentDocumentById('DOC-ACT-2023') || GOVERNMENT_DOCUMENTS[0];
  }

  return getGovernmentDocumentById('DOC-ACT-2023') || GOVERNMENT_DOCUMENTS[0];
}

/**
 * Returns formatted badge information and direct PDF link for rendering in UI cards and drawers
 */
export interface GovernmentLinkageInfo {
  documentId: string;
  shortTitle: string;
  officialTitle: string;
  gazetteNotification: string;
  gazettePart?: string;
  instrumentDate: string;
  officialUrl: string;
  sha256: string;
  lineStart?: number;
  lineEnd?: number;
  citationString: string;
  categoryLabel: string;
}

export function getGovernmentLinkage(
  item: ControlObligation | StatutoryProvision,
  isControl: boolean
): GovernmentLinkageInfo {
  const doc = getGovernmentDocumentForItem(item, isControl);
  
  let lineStart: number | undefined;
  let lineEnd: number | undefined;
  let citationString = '';

  if (isControl) {
    const ctrl = item as ControlObligation;
    const firstLegal = ctrl.legal_source_records?.[0];
    lineStart = firstLegal?.line_start;
    lineEnd = firstLegal?.line_end;
    citationString = ctrl.scope || ctrl.provision || doc.gazette_notification || '';
  } else {
    const prov = item as StatutoryProvision;
    lineStart = prov.line_start;
    lineEnd = prov.line_end;
    citationString = prov.provision || doc.gazette_notification || '';
  }

  return {
    documentId: doc.id,
    shortTitle: doc.short_title,
    officialTitle: doc.official_title,
    gazetteNotification: doc.gazette_notification || doc.short_title,
    gazettePart: doc.gazette_part,
    instrumentDate: doc.publication_date_display,
    officialUrl: doc.official_url,
    sha256: doc.sha256,
    lineStart,
    lineEnd,
    citationString,
    categoryLabel: doc.category_label
  };
}
